"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  type LandingUser,
  resolveLandingAuthState,
} from "@/components/landing/landing-auth-state";

export default function GraphifyLandingHeroSecondaryCta({
  locale,
  initialUser,
  initialHasActiveSubscription = false,
}: {
  locale: string;
  initialUser?: LandingUser | null;
  initialHasActiveSubscription?: boolean;
}) {
  return (
    <GraphifyLandingHeroSecondaryCtaWithSession
      locale={locale}
      initialUser={initialUser}
      initialHasActiveSubscription={initialHasActiveSubscription}
    />
  );
}

function GraphifyLandingHeroSecondaryCtaWithSession({
  locale,
  initialUser,
  initialHasActiveSubscription,
}: {
  locale: string;
  initialUser?: LandingUser | null;
  initialHasActiveSubscription: boolean;
}) {
  const { data: session, status } = useSession();
  const { user, signedIn } = resolveLandingAuthState(status, session, initialUser);

  if (status === "loading" && initialUser === undefined) {
    return <div className="h-12 w-56 animate-pulse rounded-full bg-white/10" />;
  }

  if (signedIn) {
    const hasActiveSubscription =
      user?.hasActiveSubscription ?? initialHasActiveSubscription;
    const href = hasActiveSubscription ? `/${locale}/graphs` : `/${locale}/pricing`;

    return (
      <Button
        asChild
        size="lg"
        variant="graphifyPrimary"
        className="rounded-full px-7"
      >
        <Link href={href} prefetch={false}>
          Build a graph
          <ArrowRight className="size-4" />
        </Link>
      </Button>
    );
  }

  return <SignedOutHeroCta locale={locale} />;
}

function SignedOutHeroCta({ locale }: { locale: string }) {
  return (
    <Button
      asChild
      size="lg"
      variant="graphifyPrimary"
      className="rounded-full px-7"
    >
      <Link
        href={`/${locale}/auth/signin?callbackUrl=${encodeURIComponent(`/${locale}/graphs`)}`}
      >
        Sign in to Build a graph
        <ArrowRight className="size-4" />
      </Link>
    </Button>
  );
}
